// local
import { trackEvent } from '../analytics/helpers.js';
import { setStyle } from '../util/dom.js';

/**
 *
 * @param {string} query
 * @param {() => void} callback
 */
function onMatchMedia(query, callback) {
  // create our media query
  const mediaQueryList = window.matchMedia(query);

  // set up our change function
  function onChange() {
    if (mediaQueryList.matches) {
      callback();
    }
  }

  // listen for changes
  mediaQueryList.addEventListener('change', onChange);

  // call our callback immediately if the media query is already active
  onChange();
}

/**
 * @param {Element} el
 * @param {number} delta
 */
export function updateScrollPosition(el, delta) {
  el.scrollLeft = el.scrollLeft + delta;
}

/**
 * @param {Element} el
 */
function getDimension(el) {
  const { left, right, width } = el.getBoundingClientRect();

  return {
    start: Math.round(left),
    end: Math.round(right),
    length: Math.round(width),
  };
}

/**
 * @param {Element} container
 * @param {Element} el
 * @param {boolean} [immediate]
 */
function scrollContainerToElement(container, el, immediate = false) {
  const snapOffset = getDimension(el).start;
  const scrollOffset = getDimension(container).start;
  const delta = Math.round(snapOffset - scrollOffset);

  if (immediate) {
    setStyle(container, 'scroll-behavior', 'auto');
  } else {
    setStyle(container, 'scroll-behavior', 'smooth');
  }
  updateScrollPosition(container, delta);

  if (immediate) {
    setStyle(container, 'scroll-behavior', 'smooth');
  }
}

/**
 * @param {Element} container
 * @param {number} current
 * @param {number} max
 */
function updateNavCircles(container, current, max) {
  const circlesContainer = container.querySelector('.nav-circles');
  const dots = circlesContainer.querySelectorAll('.nav-dot');
  const numberOfDots = dots.length;
  const diff = max - numberOfDots;
  const shouldAddDots = diff > 0;

  for (let i = 0; i < Math.abs(diff); i++) {
    if (shouldAddDots) {
      const dot = document.createElement('div');
      dot.classList.add('nav-dot');
      circlesContainer.appendChild(dot);
    } else {
      circlesContainer.removeChild(circlesContainer.lastChild);
    }
  }

  // query again to pick up any new dots
  circlesContainer.querySelectorAll('.nav-dot').forEach((dot, index) => {
    if (index === current) {
      dot.classList.add('active');
    } else {
      dot.classList.remove('active');
    }
  });
}

/**
 * @param {number} slideIndex
 * @param {number} numberOfDots
 * @param {Element} leftArrow
 * @param {Element} rightArrow
 */
function updateArrows(slideIndex, numberOfDots, leftArrow, rightArrow) {
  if (slideIndex === 0) {
    leftArrow.classList.remove('active');
  } else {
    leftArrow.classList.add('active');
  }

  if (slideIndex === numberOfDots - 1) {
    rightArrow.classList.remove('active');
  } else {
    rightArrow.classList.add('active');
  }
}

function setup(/** @type {Element} */ container) {
  const placement = container.getAttribute('data-placement');
  const label = container.getAttribute('data-default-tab');

  container.addEventListener('tmp_hotzone_will_appear', () => {
    trackEvent('topshelf', 'view', label);
  });

  container.addEventListener('click', (event) => {
    if (event.target.matches('a')) {
      trackEvent('topshelf', 'item-click', label);
    }
  });

  const scrollContainer = container.querySelector('ul');
  const scrollItems = scrollContainer.querySelectorAll('li');
  const numberOfItems = scrollItems.length;
  let numberVisible = 4;
  let numberOfDots = Math.ceil(numberOfItems / numberVisible);
  let slideIndex = 0;

  const leftArrowButton = container.querySelector('.left-arrow');
  const rightArrowButton = container.querySelector('.right-arrow');

  function updateNumberOfVisibleSlides(/** @type {number} */ amount) {
    // set the number of visible slides for this shelf
    numberVisible = amount;
    // update the number of dots for this shelf
    numberOfDots = Math.ceil(numberOfItems / numberVisible);
    // modify the visible dots to match the new number of dots
    updateNavCircles(container, slideIndex, numberOfDots);
    // check if arrows need to be enabled/disabled as a result
    updateArrows(slideIndex, numberOfDots, leftArrowButton, rightArrowButton);
    // make sure the visible number of items is correct
    scrollItems.forEach((item) => {
      setStyle(item, 'flex', `0 0 ${100 / numberVisible}%`);
    });
    // immediately scroll to the right location based on the updates
    scrollContainerToElement(
      scrollContainer,
      scrollItems[slideIndex * numberVisible],
      true
    );
  }

  onMatchMedia('(max-width: 600px)', () => updateNumberOfVisibleSlides(1));
  onMatchMedia('(min-width: 601px) and (max-width: 800px)', () =>
    updateNumberOfVisibleSlides(2)
  );
  onMatchMedia('(min-width: 801px) and (max-width: 1140px)', () =>
    updateNumberOfVisibleSlides(3)
  );
  onMatchMedia('(min-width: 1141px)', () => updateNumberOfVisibleSlides(4));

  leftArrowButton.addEventListener('click', () => {
    slideIndex = Math.max(slideIndex - 1, 0);
    scrollContainerToElement(
      scrollContainer,
      scrollItems[slideIndex * numberVisible]
    );
    updateNavCircles(container, slideIndex, numberOfDots);
    updateArrows(slideIndex, numberOfDots, leftArrowButton, rightArrowButton);
    trackEvent(`topshelf:${placement}`, 'left_arrow');
  });

  rightArrowButton.addEventListener('click', () => {
    slideIndex = Math.min(slideIndex + 1, numberOfDots - 1);
    scrollContainerToElement(
      scrollContainer,
      scrollItems[slideIndex * numberVisible]
    );
    updateNavCircles(container, slideIndex, numberOfDots);
    updateArrows(slideIndex, numberOfDots, leftArrowButton, rightArrowButton);
    trackEvent(`topshelf:${placement}`, 'right_arrow');
  });

  window.addEventListener('resize', () => {
    scrollContainerToElement(
      scrollContainer,
      scrollItems[slideIndex * numberVisible],
      true
    );
  });
}

function initialize() {
  const shelfElements = document.querySelectorAll(
    '.topshelf, .topshelf-collection'
  );

  shelfElements.forEach((element) => {
    setup(element);
  });
}

initialize();
